/**
 * Toy Lane custom: header search bar.
 *
 * Desktop: typing in the header bar opens a dropdown under it.
 * Mobile: tapping the header bar opens a full-screen search panel with a back arrow.
 * Empty box shows trending products; typing shows live results from Shopify's
 * predictive search, rendered by sections/tl-search-results.liquid.
 *
 * Works without this script too: the bar is a normal search form.
 */

const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
const DEBOUNCE_MS = 220;
const RESULT_LIMIT = '6';

class TlSearch extends HTMLElement {
  #abortController = null;
  #debounceTimer = null;
  #cache = new Map();
  #isOpen = false;
  #historyPushed = false;
  #pendingUrl = null;
  #lastTerm = '';

  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.barInput = this.querySelector('[data-tl-bar] [data-tl-input]');
    this.panel = this.querySelector('[data-tl-panel]');
    this.panelInput = this.panel?.querySelector('[data-tl-input]');
    this.results = this.panel?.querySelector('[data-tl-results]');
    this.trendingTemplate = this.querySelector('template[data-tl-trending]');

    if (!this.barInput || !this.panel || !this.panelInput || !this.results) return;

    this.#applyMode();
    MOBILE_QUERY.addEventListener('change', this.#onModeChange);

    this.barInput.addEventListener('focus', this.#onBarFocus);
    this.barInput.addEventListener('click', this.#onBarClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.panel.addEventListener('keydown', this.#onKeydown);

    for (const input of [this.barInput, this.panelInput]) {
      input.addEventListener('input', this.#onInput);
    }

    for (const form of [this.querySelector('[data-tl-bar]'), this.panel.querySelector('[data-tl-bar]')]) {
      form?.addEventListener('submit', this.#onSubmit);
    }

    this.addEventListener('click', this.#onClick);
    this.panel.addEventListener('click', this.#onClick);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    window.addEventListener('popstate', this.#onPopState);
  }

  disconnectedCallback() {
    MOBILE_QUERY.removeEventListener('change', this.#onModeChange);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    window.removeEventListener('popstate', this.#onPopState);
    // If the theme editor removes the header while the mobile panel is open, clean up
    if (this.panel?.parentElement === document.body) this.panel.remove();
    document.documentElement.classList.remove('tl-search-lock');
    delete this.dataset.ready;
  }

  /** Listeners sit on both the component and the panel (the panel moves to <body> on mobile).
   *  While the panel is inside the component, handle each event once. */
  #isDuplicate(event) {
    return event.currentTarget === this && this.panel.contains(/** @type {Node} */ (event.target));
  }

  get #isMobile() {
    return MOBILE_QUERY.matches;
  }

  get #activeInput() {
    return this.#isMobile ? this.panelInput : this.barInput;
  }

  /* ---------- Mode ---------- */

  #applyMode() {
    // On mobile the header bar only opens the panel, so it must not raise the keyboard itself.
    this.barInput.readOnly = this.#isMobile;
    this.barInput.setAttribute('aria-haspopup', this.#isMobile ? 'dialog' : 'listbox');
  }

  #onModeChange = () => {
    this.close({ restoreFocus: false });
    this.#applyMode();
  };

  /* ---------- Open / close ---------- */

  open() {
    if (this.#isOpen) return;
    this.#isOpen = true;

    if (this.#isMobile) {
      this.panelInput.value = this.barInput.value;
      document.body.append(this.panel);
      document.documentElement.classList.add('tl-search-lock');
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.panel.setAttribute('aria-label', 'Search');
      try {
        history.pushState({ tlSearch: true }, '');
        this.#historyPushed = true;
      } catch (error) {
        this.#historyPushed = false;
      }
    } else {
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      this.panel.removeAttribute('aria-label');
    }

    this.panel.hidden = false;
    this.barInput.setAttribute('aria-expanded', 'true');
    this.#toggleClearButtons();
    this.#render(this.#activeInput.value);

    if (this.#isMobile) this.panelInput.focus({ preventScroll: true });
  }

  close({ restoreFocus = true, fromHistory = false } = {}) {
    if (!this.#isOpen) return;
    this.#isOpen = false;

    const wasMobilePanel = this.panel.parentElement === document.body;

    if (wasMobilePanel) this.barInput.value = this.panelInput.value;

    this.panel.hidden = true;
    this.barInput.setAttribute('aria-expanded', 'false');
    this.#toggleClearButtons();

    if (wasMobilePanel) {
      this.append(this.panel);
      document.documentElement.classList.remove('tl-search-lock');
    }

    if (this.#historyPushed) {
      this.#historyPushed = false;
      if (!fromHistory) history.back();
    }

    if (restoreFocus && wasMobilePanel) this.barInput.focus({ preventScroll: true });
  }

  /**
   * Leaves the page from inside the mobile panel. Removes the extra history entry first,
   * so the phone's back button returns to the page the shopper searched from.
   */
  #navigate(url) {
    // Drop focus first so the iPhone keyboard closes before the results page loads
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (this.#historyPushed) {
      this.#pendingUrl = url;
      this.#historyPushed = false;
      history.back();
      // Fallback in case popstate never fires
      window.setTimeout(() => {
        if (this.#pendingUrl) {
          const pending = this.#pendingUrl;
          this.#pendingUrl = null;
          window.location.assign(pending);
        }
      }, 400);
    } else {
      window.location.assign(url);
    }
  }

  #onPopState = () => {
    if (this.#pendingUrl) {
      const pending = this.#pendingUrl;
      this.#pendingUrl = null;
      window.location.assign(pending);
      return;
    }
    if (this.#isOpen && this.#historyPushed) {
      this.#historyPushed = false;
      this.close({ fromHistory: true });
    }
  };

  /* ---------- Events ---------- */

  #onBarFocus = () => {
    if (!this.#isMobile) this.open();
  };

  #onBarClick = () => {
    this.open();
  };

  #onInput = (event) => {
    const value = event.target.value;
    // Keep both boxes in step
    if (event.target === this.panelInput) this.barInput.value = value;
    else this.panelInput.value = value;

    this.#toggleClearButtons();
    if (!this.#isOpen) this.open();

    window.clearTimeout(this.#debounceTimer);
    if (value.trim() === '') {
      this.#render('');
      return;
    }
    this.#debounceTimer = window.setTimeout(() => this.#render(value), DEBOUNCE_MS);
  };

  #onSubmit = (event) => {
    const value = this.#activeInput.value.trim();
    if (value === '') {
      event.preventDefault();
      this.#activeInput.focus();
      return;
    }
    this.#activeInput.blur();
    if (this.#historyPushed) {
      event.preventDefault();
      const form = /** @type {HTMLFormElement} */ (event.currentTarget);
      const url = new URL(form.action, window.location.origin);
      for (const [key, val] of new FormData(form)) url.searchParams.set(key, String(val));
      this.#navigate(url.toString());
    }
  };

  #onClick = (event) => {
    if (this.#isDuplicate(event)) return;
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-tl-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    if (target.closest('[data-tl-clear]')) {
      event.preventDefault();
      this.barInput.value = '';
      this.panelInput.value = '';
      this.#toggleClearButtons();
      if (this.#isOpen) this.#render('');
      this.#activeInput.focus();
      return;
    }

    const link = target.closest('a[data-tl-result]');
    if (link && this.#historyPushed && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      this.#navigate(link.href);
    }
  };

  #onFocusOut = (event) => {
    if (this.#isMobile || !this.#isOpen) return;
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (next && this.contains(next)) return;
    // Wait a frame so clicks on results register before closing
    window.requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) this.close({ restoreFocus: false });
    });
  };

  #onDocumentPointerDown = (event) => {
    if (!this.#isOpen || this.#isMobile) return;
    if (!this.contains(/** @type {Node} */ (event.target))) this.close({ restoreFocus: false });
  };

  #onKeydown = (event) => {
    if (this.#isDuplicate(event)) return;
    if (event.key === 'Escape' && this.#isOpen) {
      event.preventDefault();
      this.close();
      if (!this.#isMobile) this.barInput.blur();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (!this.#isOpen) return;

    const links = /** @type {HTMLElement[]} */ ([...this.results.querySelectorAll('a[data-tl-result]')]);
    if (links.length === 0) return;

    const active = document.activeElement;
    const index = links.indexOf(/** @type {HTMLElement} */ (active));
    const inInput = active === this.barInput || active === this.panelInput;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (inInput) links[0].focus();
      else if (index > -1 && index < links.length - 1) links[index + 1].focus();
    } else {
      event.preventDefault();
      if (index > 0) links[index - 1].focus();
      else if (index === 0) this.#activeInput.focus();
    }
  };

  /* ---------- Rendering ---------- */

  #toggleClearButtons() {
    const hasValue = (this.#activeInput?.value ?? '') !== '';
    for (const button of [...this.querySelectorAll('[data-tl-clear]'), ...this.panel.querySelectorAll('[data-tl-clear]')]) {
      button.hidden = !hasValue;
    }
  }

  #showTrending() {
    this.results.removeAttribute('aria-busy');
    this.results.replaceChildren(this.trendingTemplate ? this.trendingTemplate.content.cloneNode(true) : '');
  }

  async #render(rawTerm) {
    const term = rawTerm.trim();
    this.#lastTerm = term;

    if (term === '') {
      this.#abortController?.abort();
      this.#showTrending();
      return;
    }

    if (this.#cache.has(term)) {
      this.results.removeAttribute('aria-busy');
      this.results.innerHTML = this.#cache.get(term);
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.results.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(this.dataset.url || '/search/suggest', window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', this.dataset.section || 'tl-search-results');
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', RESULT_LIMIT);
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,tag,variants.title,vendor');

      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const markup = doc.querySelector('[data-tl-results-content]')?.innerHTML ?? '';

      this.#cache.set(term, markup);
      if (this.#lastTerm === term) {
        this.results.innerHTML = markup;
        this.results.removeAttribute('aria-busy');
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (this.#lastTerm === term) this.#renderFallback(term);
    }
  }

  /** If live results fail, still offer the full search page. */
  #renderFallback(term) {
    this.results.removeAttribute('aria-busy');
    const form = this.querySelector('[data-tl-bar]');
    const url = new URL(form?.getAttribute('action') || '/search', window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('options[prefix]', 'last');

    const link = document.createElement('a');
    link.className = 'tl-search__query';
    link.href = url.toString();
    link.setAttribute('data-tl-result', '');
    const text = document.createElement('span');
    text.className = 'tl-search__query-text';
    text.textContent = `Search for “${term}”`;
    link.append(text);
    this.results.replaceChildren(link);
  }
}

if (!customElements.get('tl-search')) customElements.define('tl-search', TlSearch);

/* ---------- Sitewide fixes (this script loads on every page) ---------- */

// Search results page: the X inside the page's search box should only clear the text.
// Horizon's default X is a link that reloads /search, which shows the Trending page.
document.addEventListener(
  'click',
  (event) => {
    const reset = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('.search__reset-button') : null);
    if (!reset) return;
    const input = reset.closest('search-page-input-component')?.querySelector('input[type="search"]');
    if (!(input instanceof HTMLInputElement)) return;
    event.preventDefault();
    input.value = '';
    input.focus();
  },
  true
);

// Never open a page with the keyboard already up (iPhone keeps it open after a search)
const dropStrayFocus = () => {
  const active = document.activeElement;
  if (active instanceof HTMLInputElement && active.type === 'search') active.blur();
};
window.addEventListener('pageshow', dropStrayFocus);
